<template>
	<div class="eb-password">
		
		<div :class="['eb-input', {'eb-input-expand': width === 'expand', 'eb-input-inline': inline}]">
			<EbLabel v-if="label" :forId="uid" :required="required" :infoIcon="infoIcon" :helpText="helpText" :text="label">
				<input
				:id="uid"
				:type="inputType"
				:class="['eb-password-input', background ? 'eb-input-simple' : 'eb-input-solid']"
				:required="required"
				:disabled="disabled"
				:value="value"
				:minlength="minlength"
				@input="checkStrength($event.target.value) & $emit('input', $event.target.value)"
				/>
			</EbLabel>
		</div>
		<EbCheckbox :label=inputText class="eb-password-toggle-checkbox-label" @input="togglePasswordVisibility()" :disabled="disabled"></EbCheckbox>
		
		<div v-if="needCondition" class="sr-only">
			<span v-if="isGoodLength">✓</span>
			<span v-else>✖</span>
			{{ minlength }} caractères minimum
			
			<label>
				Indice de complexité de votre mot de passe : {{ scoreText }}
				<meter min="0" max="4" low="2" high="3" optimum="4" :value="score">{{ scoreText }}</meter>
			</label>
		</div>
		
		<div v-if="needCondition" class="eb-password-notice">
			<div class="eb-meter">
				<p class="eb-meter-title">
					Force du mot de passe <span :class="'eb-meter-' + score">{{ scoreText }}</span>
				</p>
				<div class="eb-meter-gauge">
					<span :class="'eb-meter-gauge-' + score">
						<span class="sr-only">{{ scoreText }}</span>
					</span>
				</div>
			</div>
			<p>Le mot de passe doit contenir : au moins {{ minlength }} caractères.
				<span v-if="isGoodLength" class="eb-password-notice-success">
					<font-awesome-icon :icon="['fas', 'check']" />
					<span class="sr-only">Condition validée</span>
				</span>
				<span v-else class="eb-password-notice-error">
					<font-awesome-icon :icon="['fas', 'times']" />
					<span class="sr-only">Condition non-validée</span>
				</span>
			</p>
			
		</div>
		
	</div>
</template>

<script>
export default {
	name: "EbPassword",
	status: "ready",
	release: "1.0.0",
	props: {
		label: {
			type: String,
			default: null,
		},
		required: {
			type: Boolean,
			default: false,
		},
		value: {
			type: String,
			default: "",
		},
		minlength: {
			default: 12,
		},
		background: {
			type: Boolean,
			default: false,
		},
		needCondition: {
			type: Boolean,
			default: false,
		},
		width: {
			type: String,
			default: "auto",
			validator: value => {
				return value.match(/(auto|expand)/)
			},
		},
		inline: {
			type: Boolean,
			default: false,
		},
		infoIcon: {
			type: Boolean,
			default: false,
		},
		helpText: {
			type: Boolean,
			default: null,
		},
		disabled: {
			type: Boolean,
			default: false,
		}		
	},
	data() {
		return {
			isVisible: false,
			score: 0,
			scoreText: "Faible",
			uid: null,
		}
	},
	computed: {
		inputText: function() {
			return this.isVisible ? "Masquer le mot de passe" : "Afficher le mot de passe"
		},
		inputType: function() {
			return this.isVisible ? "text" : "password"
		},
		isGoodLength: function() {
			if (!this.value) return false
			return this.value.length >= this.minlength ? true : false
		},
	},
	mounted() {
		this.uid = `uid_${this._uid}`
	},
	methods: {
		togglePasswordVisibility: function() {
			this.isVisible = !this.isVisible
		},
		scorePassword: function(pass) {
			let score = 0,
			length = 0,
			specialChar = 0,
			caseMix = 0,
			numCharMix = 0
			
			const specialCharRegex = /[^A-Za-z0-9]/g,
			lowercaseRegex = /(.*[a-z].*)/g,
			uppercaseRegex = /(.*[A-Z].*)/g,
			numberRegex = /(.*[0-9].*)/g,
			repeatCharRegex = /(\w)(\1+\1+\1+\1+)/g,
			hasSpecialChar = specialCharRegex.test(pass),
			hasLowerCase = lowercaseRegex.test(pass),
			hasUpperCase = uppercaseRegex.test(pass),
			hasNumber = numberRegex.test(pass),
			hasRepeatChars = repeatCharRegex.test(pass)
			
			if (pass.length > 4) {
				if ((hasLowerCase || hasUpperCase) && hasNumber) numCharMix = 1
				
				if (hasUpperCase && hasLowerCase) caseMix = 1
				if ((hasLowerCase || hasUpperCase || hasNumber) && hasSpecialChar) specialChar = 1
				
				if (pass.length > this.minlength) length = 1
				if (pass.length > 15 && !hasRepeatChars) length = 2
				if (pass.length > 25 && !hasRepeatChars) length = 3
				
				score = length + specialChar + caseMix + numCharMix
				
				if (score > 4) score = 4
			}
			
			this.score = score
		},
		nameScore: function() {
			switch (this.score) {
				case 2:
				this.scoreText = "Moyen"
				break
				case 3:
				this.scoreText = "Fort"
				break
				case 4:
				this.scoreText = "Excellent"
				break
				default:
				this.scoreText = "Faible"
			}
		},
		checkStrength: function(pass) {
			this.scorePassword(pass)
			this.nameScore()
		},
	},
}
</script>

<style lang="scss" scoped>
.eb-password {
	display: flex;
	flex-wrap: wrap;
	position: relative;
	@include stack-space($eb-space-m);
	
	&-input {
		order: 0;
		padding-right: 30%;
	}
	
	&-toggle {
		&-checkbox {
			&:checked {
				& ~ .eb-password-input {
					text-security: disc;
					-webkit-text-security: disc;
					-moz-text-security: disc; // doesn't work :'|
				}
				
				& ~ .eb-password-toggle-checkbox-label {
					.eb-password-toggle-txt-show {
						display: inline-block;
					}
					.eb-password-toggle-txt-hide {
						display: none;
					}
				}
			}
		}
		&-checkbox-label {
			text-align: right;
			cursor: pointer;
			color: $eb-color-blue-secondary;
			font-size: $eb-font-size-xs;
			margin-right: 0;
		}
		&-txt-show {
			display: none;
		}
		&-txt-hide {
			display: inline-block;
		}
	}
	&-notice {
		flex-basis: 100%;
		&-success {
			@include inset-space($eb-space-default);
			color: $eb-color-success;
		}
		&-error {
			@include inset-space($eb-space-default);
			color: $eb-color-error;
		}
	}
	
	meter {
		width: 100%;
	}
	
	.eb-meter {
		&-title {
			display: flex;
			justify-content: space-between;
			align-items: center;
			font-size: $eb-font-size-m;
			font-weight: $eb-font-weight-semibold;
			color: $black;
			margin-right: $eb-space-s;
		}
		
		&-0, &-1 {
			color: $eb-color-error;
			font-size: $eb-font-size-xs;
		}
		&-2 {
			color: $eb-color-warning;
			font-size: $eb-font-size-xs;
		}
		&-3 {
			color: $eb-color-success;
			font-size: $eb-font-size-xs;
		}
		&-4 {
			color: $eb-color-success-dark;
			font-size: $eb-font-size-xs;
		}
		
		&-gauge {
			width: 100%;
			height: $eb-space-m;
			position: relative;
			background: $white;
			border: $eb-border-default solid $eb-color-gray-600;
			padding: 2px;
			margin-bottom: $eb-space-m;
			border-radius: $eb-radius-l;
			color: $black;
			
			span[class*="eb-meter-gauge"] {
				display: flex;
				justify-content: center;
				align-items: center;
				height: 100%;
				border-radius: $eb-radius-l;
				position: relative;
				overflow: hidden;
			}
			
			&-1 {
				background-color: $eb-color-error;
				width: 25%;
			}
			&-2 {
				background-color: $eb-color-warning;
				width: 50%;
			}
			&-3 {
				background-color: $eb-color-success;
				width: 75%;
			}
			&-4 {
				background-color: $eb-color-success-dark;
				width: 100%;
			}
		}
	}
	
	@include respond-to("large-up") {
		&-notice {
			margin: $eb-space-l $eb-space-xxl $eb-space-l $eb-space-xxl;
		}
	}
}
</style>
